import { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Spinner,
  Badge,
  Form,
  InputGroup,
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL, HEADERS } from "../../../utils/constant";

const AdminDetailsModal = ({ show, onHide, adminId, fetchAdmins }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSmsForm, setShowSmsForm] = useState(false);
  const [smsCount, setSmsCount] = useState("");
  const [updating, setUpdating] = useState(false);

  const fetchAdminDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/users/tms_users/${adminId}`, {
        headers: HEADERS,
      });
      if (res.status === 200) setAdmin(res.data);
    } catch {
      toast.error("Failed to load admin details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminId) fetchAdminDetails();
  }, [adminId]);

  const handleUpdateSmsCount = async (e) => {
    e.preventDefault();
    if (!smsCount || isNaN(smsCount) || smsCount <= 0) {
      toast.warning("Please enter a valid SMS count");
      return;
    }
    try {
      setUpdating(true);
      const payload = { sms_hit_count: smsCount };
      const res = await axios.put(
        `${BASE_URL}/users/tms_users/${adminId}/`,
        payload,
        {
          headers: HEADERS,
        },
      );
      if (res.status === 200) {
        toast.success("SMS count updated successfully");
        setShowSmsForm(false);
        setSmsCount("");
        fetchAdminDetails();
        fetchAdmins();
      }
    } catch {
      toast.error("Failed to update SMS count");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      className="admin-details-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold text-primary">
          Admin Details
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : admin ? (
          <div className="compact-admin-details">
            <div className="profile-header d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
                  style={{ width: 55, height: 55, fontSize: "1.4rem" }}
                >
                  {admin.first_name?.[0]?.toUpperCase() || "A"}
                </div>
                <div>
                  <h5 className="mb-1">
                    {admin.first_name} {admin.last_name}
                  </h5>
                  <small className="text-muted">{admin.email}</small>
                  <div className="mt-1">
                    <Badge
                      bg={admin.is_active ? "success" : "secondary"}
                      className="me-1"
                    >
                      {admin.is_active ? "Active" : "Inactive"}
                    </Badge>
                    <Badge bg="info">{admin.role}</Badge>
                  </div>
                </div>
              </div>
              <div className="text-end small">
                <div>Joined: {admin.created_at || "N/A"}</div>
                <div>
                  SMS Left:{" "}
                  <Badge bg="warning" text="dark">
                    {admin.sms_hit_count ?? 0}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="text-end mb-3">
              {!showSmsForm ? (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowSmsForm(true)}
                >
                  + Update SMS Count
                </Button>
              ) : (
                <Form onSubmit={handleUpdateSmsCount}>
                  <InputGroup
                    size="sm"
                    className="justify-content-end"
                    style={{ maxWidth: "300px", marginLeft: "auto" }}
                  >
                    <Form.Control
                      type="number"
                      placeholder="Enter new count"
                      value={smsCount}
                      onChange={(e) => setSmsCount(e.target.value)}
                      min="1"
                      required
                    />
                    <Button variant="success" type="submit" disabled={updating}>
                      {updating ? "Updating..." : "Save"}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowSmsForm(false)}
                    >
                      Cancel
                    </Button>
                  </InputGroup>
                </Form>
              )}
            </div>
            <div className="details-section">
              <h6 className="fw-bold text-primary border-bottom pb-1">
                Personal Info
              </h6>
              <Row className="small text-muted mb-3">
                <Col md={4}>
                  <strong>Phone:</strong> {admin.phone_number || "N/A"}
                </Col>
                <Col md={4}>
                  <strong>DOB:</strong> {admin.dob || "N/A"}
                </Col>
                <Col md={4}>
                  <strong>Country:</strong> {admin.country || "N/A"}
                </Col>
                <Col md={6}>
                  <strong>Address:</strong> {admin.primary_address || "N/A"}
                </Col>
                <Col md={6}>
                  <strong>Notes:</strong> {admin.notes || "N/A"}
                </Col>
              </Row>

              <h6 className="fw-bold text-primary border-bottom pb-1">
                Company Info
              </h6>
              <Row className="small text-muted mb-3">
                <Col md={4}>
                  <strong>Name:</strong> {admin.company_name || "N/A"}
                </Col>
                <Col md={4}>
                  <strong>City:</strong> {admin.companyCity || "N/A"}
                </Col>
                <Col md={4}>
                  <strong>State:</strong> {admin.companyState || "N/A"}
                </Col>
                <Col md={4}>
                  <strong>GST:</strong> {admin.gstNo || "N/A"}
                </Col>
                <Col md={4}>
                  <strong>PAN:</strong> {admin.panNo || "N/A"}
                </Col>
                <Col md={4}>
                  <strong>Website:</strong> {admin.companyWebsite || "N/A"}
                </Col>
              </Row>

              <h6 className="fw-bold text-primary border-bottom pb-1">
                Bank Info
              </h6>
              <Row className="small text-muted mb-3">
                <Col md={4}>
                  <strong>Bank:</strong> {admin.bankName || "N/A"}
                </Col>
                <Col md={4}>
                  <strong>IFSC:</strong> {admin.ifscSwift || "N/A"}
                </Col>
                <Col md={4}>
                  <strong>Account No:</strong> {admin.accountNumber || "N/A"}
                </Col>
                <Col md={4}>
                  <strong>Branch:</strong> {admin.branchAddress || "N/A"}
                </Col>
                <Col md={4}>
                  <strong>UPI:</strong> {admin.upiId || "N/A"}
                </Col>
              </Row>

              {/* <h6 className="fw-bold text-primary border-bottom pb-1">System Info</h6>
              <Row className="small text-muted">
                <Col md={4}><strong>Subscription:</strong> {admin.subscription || "N/A"}</Col>
                <Col md={4}><strong>Employees:</strong> {admin.employees_created}/{admin.max_employees_allowed}</Col>
                <Col md={4}><strong>GPS:</strong> {admin.gps_status ? "Enabled" : "Disabled"}</Col>
              </Row> */}
            </div>
          </div>
        ) : (
          <p className="text-center text-muted">No admin details found.</p>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>

      <style jsx>{`
        .compact-admin-details {
          font-size: 0.9rem;
        }
        .details-section h6 {
          margin-top: 1rem;
        }
        .small.text-muted strong {
          color: #333;
        }
      `}</style>
    </Modal>
  );
};

export default AdminDetailsModal;
